import React from "react";
import { Check, Plane } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { CONTACT } from "../../lib/constants";
import { useReveal } from "../../hooks/useReveal";

export const Packages = () => {
  const { t } = useLanguage();
  const ref = useReveal();

  return (
    <section
      id="packages"
      data-testid="packages-section"
      className="bg-bg-base py-16 md:py-20"
    >
      <div ref={ref} className="reveal max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="overline">{t.packages.overline}</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-accent-dark mt-4 tracking-tight font-light">
            {t.packages.title}
          </h2>
          <p className="mt-5 text-text-muted text-base md:text-lg leading-relaxed">
            {t.packages.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.packages.items.map((p, i) => (
            <div
              key={i}
              data-testid={`package-${i}`}
              className={`relative flex flex-col p-8 transition-all duration-500 hover:-translate-y-2 ${
                p.featured
                  ? "bg-accent-dark text-white border border-gold"
                  : "bg-white border border-border-soft hover:border-gold/60"
              }`}
            >
              {p.featured && p.tag && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-white text-[10px] tracking-[0.2em] uppercase px-3 py-1">
                  {p.tag}
                </span>
              )}
              <h3
                className={`font-serif text-2xl md:text-[1.7rem] font-light leading-tight ${
                  p.featured ? "text-white" : "text-accent-dark"
                }`}
              >
                {p.name}
              </h3>
              <p
                className={`text-xs tracking-[0.2em] uppercase mt-3 ${
                  p.featured ? "text-gold" : "text-gold"
                }`}
              >
                {p.duration}
              </p>
              <p
                className={`mt-1 text-sm ${
                  p.featured ? "text-white/70" : "text-text-muted"
                }`}
              >
                {p.photos}
              </p>

              <div
                className={`mt-6 mb-6 pb-6 border-b ${
                  p.featured ? "border-white/15" : "border-border-soft"
                }`}
              >
                <p
                  className={`font-serif text-3xl md:text-4xl font-light ${
                    p.featured ? "text-white" : "text-accent-dark"
                  }`}
                >
                  {p.price}
                </p>
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {p.features.map((f, j) => (
                  <li
                    key={j}
                    className={`flex items-start gap-2.5 text-sm ${
                      p.featured ? "text-white/80" : "text-text-muted"
                    }`}
                  >
                    <Check
                      size={14}
                      className={`mt-1 shrink-0 ${
                        p.featured ? "text-gold" : "text-gold"
                      }`}
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <a
                href={CONTACT.bookingForm}
                target="_blank"
                rel="noreferrer"
                data-testid={`package-cta-${i}`}
                className={`btn-luxury w-full ${
                  p.featured ? "" : "btn-luxury-dark"
                }`}
                style={{ padding: "0.85rem 1rem", fontSize: "0.7rem" }}
              >
                {t.nav.cta}
              </a>
            </div>
          ))}
        </div>

        <div
          data-testid="package-addon"
          className="mt-10 flex flex-col md:flex-row items-center justify-between gap-6 border border-border-soft p-8 bg-white"
        >
          <div className="flex items-center gap-5">
            <div className="w-12 h-12 rounded-full bg-bg-alt flex items-center justify-center text-gold">
              <Plane size={20} />
            </div>
            <div>
              <p className="font-serif text-xl md:text-2xl text-accent-dark">
                {t.packages.addon.title}
              </p>
              <p className="text-sm text-text-muted mt-1">
                {t.packages.addon.desc}
              </p>
            </div>
          </div>
          <p className="font-serif text-2xl text-gold">
            {t.packages.addon.price}
          </p>
        </div>
      </div>
    </section>
  );
};
